import { useArtworkStore } from "@/store/artwork";
import { aegis } from "@/utils/monitoring";
import { FileDownloader, generateDownloadUrl, getImageInfo } from "@xmiles/holopix-helper";

export {
  clearTokenCookie,
  deleteCookieByName,
  getCookieByName,
  getCookieConfig,
  getEnvironmentId,
  setTokenCookie,
} from "./authCookie";

export const extractFileName = (url: string) => {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split("/");
    const fileName = pathParts.pop() || ""; // 处理空路径
    return fileName;
  } catch (e) {
    console.error("Invalid URL:", e);
    return "";
  }
};

export async function downloadFile(fileUrl: string, fileName?: string, retryCount = 3) {
  // 如果是 data URL 或 blob URL，直接下载
  if (fileUrl.startsWith("data:") || fileUrl.startsWith("blob:")) {
    downloadFileByLink(fileUrl, fileName || nanoid());
    return fileUrl;
  }
  const _fileName = extractFileName(fileUrl);
  const downloadUrl = generateDownloadUrl(fileUrl, _fileName);

  return new Promise(async (resolve, reject) => {
    const downloader = new FileDownloader(downloadUrl, {
      chunkSize: 5 * 1024 * 1024, // 5MB/片
      maxConcurrency: 4, // 4并发
      maxRetries: 5, // 最大重试5次
      retryDelay: 2000, // 基础延迟2秒
      progress: (status) => {
        if (status.chunkIndex !== undefined) {
          console.log(`[分片${status.chunkIndex}] 进度: ${status.percentage}%`);
        } else if (status.error) {
          console.error("下载失败:", status.error);
          reject(status.error);
        } else if (status.status === "completed") {
          console.log("✅ 下载完成");
          resolve(downloadUrl);
        }
      }
    });

    // 开始下载
    const blob = await downloader.start();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = _fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }).catch((error) => {
    aegis?.error({
      msg: "下载失败",
      ext1: error,
      ext2: downloadUrl
    });

    window.open(downloadUrl);
    notification.error({
      placement: "topRight",
      duration: 0,
      message: "下载失败？",
      description: h("p", {}, [
        "请确认您的网络是否正常，正常后再",
        h(
          "a",
          {
            style: {
              color: "#1890ff",
              textDecoration: "underline",
              cursor: "pointer"
            },
            target: "_blank",
            href: downloadUrl,
            download: _fileName
          },
          "点击手动下载保存"
        )
      ])
    });

    throw error;
  });
}

interface ImageSize {
  width: number;
  height: number;
}
export async function getLocalImageSize(file: File): Promise<ImageSize>;
export async function getLocalImageSize(fileList: File[]): Promise<ImageSize[]>;
export async function getLocalImageSize(file: File | File[]) {
  if (Array.isArray(file)) {
    return Promise.all(
      file.map(async (fileItem) => {
        return await getImageSize(fileItem);
      })
    );
  } else {
    return getImageSize(file);
  }
}
async function getImageSize(file: File): Promise<ImageSize> {
  try {
    // 使用 createImageBitmap 直接解码图片，无需创建 Image 元素
    const bitmap = await createImageBitmap(file);
    console.log('b',bitmap);
    
    const { width, height } = bitmap;
    // 释放 bitmap 资源
    bitmap.close();
    return { width, height };
  } catch (error) {
    // 降级方案：如果 createImageBitmap 失败，使用传统方式
    return new Promise((resolve) => {
      try {
        const imageUrl = URL.createObjectURL(file);
        const image = new Image();
        image.src = imageUrl;

        image.onload = () => {
          resolve({
            width: image.width,
            height: image.height
          });
          URL.revokeObjectURL(imageUrl);
        };

        image.onerror = () => {
          URL.revokeObjectURL(imageUrl);
          resolve({ width: 0, height: 0 });
        };
      } catch {
        resolve({ width: 0, height: 0 });
      }
    });
  }
}
export async function getImageUrlSize(url: string): Promise<ImageSize> {
  // 视频文件（或视频截图 URL）不能用 OSS image/info 接口获取元数据，直接用 new Image() 加载缩略图获取尺寸
  const isVideoUrl = /\.(mp4|webm|mov|avi|mkv|flv|m4v|wmv)(\?|$)/i.test(url)
    || /video\/snapshot/.test(url);

  if (!isVideoUrl) {
    try {
      // 剔除 URL 中已存在的 OSS 处理参数（如 ?x-oss-process=video/snapshot,t_0,f_jpg）。
      // 原因：getImageInfo 内部会再拼接 ?x-oss-process=image/info，若传入 url 已带 ?x-oss-process=...，
      // 将产生重复的 ? 导致 400 Bad Request。
      const pureUrl = url.split("?x-oss-process=")[0];
      const info = await getImageInfo(pureUrl);
      if (info.width > 0 && info.height > 0) {
        return { width: info.width, height: info.height };
      }
    } catch {
      // ignore
    }
  }
  return new Promise((resolve) => {
    try {
      const image = new Image();
      image.src = url;
      image.onload = () => {
        resolve({ width: image.width, height: image.height });
      };
      image.onerror = () => {
        resolve({ width: 0, height: 0 });
      };
    } catch (error) {
      resolve({ width: 0, height: 0 });
    }
  });
}

export const base64ToBlob = (code: string) => {
  const parts = code.split(";base64,");
  const contentType = parts[0].split(":")[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;

  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }
  return new Blob([uInt8Array], { type: contentType });
};

export function base64ToFile(code: string, filename = "image.png") {
  const blob = base64ToBlob(code);

  return new File([blob], filename, { type: "image/png" });
}

export function getFileBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

export function fetchImageAsFile(url: string) {
  return fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok " + response.statusText);
      }
      return response.blob();
    })
    .then((blob) => {
      return new File([blob], "image.png", { type: "image/png" });
    });
}

export function createRandomString() {
  return `${String(Number(new Date()))}${String((Math.random() * 100000).toFixed(0))}`;
}

export function jsonParse(val: unknown) {
  try {
    if (Object.prototype.toString.call(val) === "[object Object]" || Object.prototype.toString.call(val) === "[Object Object]") {
      return val;
    }
    return JSON.parse(val as any);
  } catch (err) {
    // console.error(err);
    return undefined;
  }
}

// 随机获取 length 长度的字符串
export function getRandomString(length = 4) {
  const characters = "abcdefghijklmnopqrstuvwxyz0123456789";
  let randomString = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    randomString += characters.charAt(randomIndex);
  }
  return randomString;
}

// 获取文件名后缀
export function getFileExtension(filename: string) {
  // 移除问号及其后面的参数
  const cleanFilename = filename.split("?")[0];
  return cleanFilename.split(".").pop() || "";
}

// 将 jpg 格式图片转 png
export const getPngBlob = (path: string): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const c = document.createElement("canvas");
    const ctx = c.getContext("2d");

    const img = new Image();
    img.crossOrigin = "";
    img.src = path;

    img.onload = () => {
      c.width = img.width;
      c.height = img.height;
      ctx?.drawImage(img, 0, 0);

      c.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject();
        }
      }, "image/png");
    };

    img.onerror = () => {
      reject();
    };
  });
};

// 画面设置-尺寸比例
export const aspectRatios = [
  {
    label: "256x256",
    showValue: "1:1",
    value: "3:3",
    useModelCkptType: ["Flux"]
  },
  {
    label: "512x512",
    showValue: "1:1",
    value: "2:2",
    useModelCkptType: ["Flux"]
  },
  {
    label: "1024x1024",
    value: "1:1"
  },
  {
    label: "1152x896",
    value: "4:3"
  },
  {
    label: "896x1152",
    value: "3:4"
  },
  {
    label: "1216x832",
    value: "3:2"
  },
  {
    label: "832x1216",
    value: "2:3"
  },
  {
    label: "1344x768",
    value: "16:9"
  },
  {
    label: "768x1344",
    value: "9:16"
  },
  {
    label: "1536x640",
    value: "21:9"
  },
  {
    label: "640x1536",
    value: "9:21"
  }
];
export function getAspectRatioSize(value: string) {
  // 兼容渲染分辨率模式（例如 "1024" | "1536"），映射为等比尺寸
  let normalized = value;
  if (value === '1024') {
    normalized = '1:1';
  } else if (value === '1536') {
    normalized = '1:1';
  }

  const [target] = aspectRatios.filter((item) => item.value === normalized);

  let width = 0;
  let height = 0;

  const [w, h] = (target || aspectRatios[0]).label.split("x");
  width = parseInt(w);
  height = parseInt(h);

  return {
    width,
    height
  };
}
// 获取 aspectRatios 中最接近的比例
export function getSimilarAspectRatios(width: number, height: number) {
  const artworkStore = useArtworkStore();
  const targetRatio = width / height;

  const _aspectRatios = aspectRatios.filter((item) => {
    return !item.useModelCkptType || item.useModelCkptType.includes(artworkStore.firstModel?.ckptType);
  });

  const ratios = _aspectRatios.map((item) => {
    const [w, h] = item.value.split(":").map(Number);
    return w / h;
  });

  let minDiff = Number.MAX_VALUE;
  let closestRatio = "";

  ratios.forEach((ratio, index) => {
    const diff = Math.abs(ratio - targetRatio);
    if (diff < minDiff) {
      minDiff = diff;
      closestRatio = _aspectRatios[index].value;
    }
  });

  return closestRatio;
}

// 将两张图片合并成一张图片
export function mergePicture(bottomImageUrl: string, topImageUrl: string, width: number, height: number): Promise<string> {
  return new Promise((resolve) => {
    const mergedCanvas = document.createElement("canvas");
    mergedCanvas.width = width;
    mergedCanvas.height = height;
    const ctxm = mergedCanvas.getContext("2d");

    const bottomImg = new Image();
    const topImg = new Image();

    bottomImg.crossOrigin = "";
    topImg.crossOrigin = "";

    bottomImg.src = bottomImageUrl;
    topImg.src = topImageUrl;

    let bottomImgLoad = false;
    let topImgLoad = false;

    bottomImg.onload = () => {
      bottomImgLoad = true;
      merge();
    };
    bottomImg.onerror = () => {
      resolve("");
    };

    topImg.onload = () => {
      topImgLoad = true;
      merge();
    };
    topImg.onerror = () => {
      resolve("");
    };

    function merge() {
      if (bottomImgLoad && topImgLoad) {
        ctxm?.drawImage(bottomImg, 0, 0);
        ctxm?.drawImage(topImg, 0, 0);

        resolve(mergedCanvas.toDataURL("image/png"));
      }
    }
  });
}

type MaskColor = [number, number, number, number];

/**
 * 将蒙板反相：有涂抹色的区域与无涂抹区域分别映射为指定颜色。
 * @param paintedReplaceColor 涂抹区域目标色；不传时保持旧行为（透明），用于 goWorkFlowV2 可传白底 [255,255,255,255]
 */
export function maskContrast(
  imageCanvas: HTMLCanvasElement,
  replaceColor: MaskColor = [0, 0, 0, 255],
  paintedReplaceColor?: MaskColor
): string {
  try {
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = imageCanvas.width;
    tempCanvas.height = imageCanvas.height;

    const tempCtx = tempCanvas.getContext("2d");

    if (tempCtx) {
      tempCtx?.drawImage(imageCanvas, 0, 0, tempCanvas.width, tempCanvas.height);
      const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      const data = imageData.data;

      // 循环遍历每个像素
      for (let i = 0; i < data.length; i += 4) {
        // 检查当前像素是否为有颜色的像素
        if (data[i] !== 0 || data[i + 1] !== 0 || data[i + 2] !== 0 || data[i + 3] !== 0) {
          if (paintedReplaceColor) {
            data[i] = paintedReplaceColor[0];
            data[i + 1] = paintedReplaceColor[1];
            data[i + 2] = paintedReplaceColor[2];
            data[i + 3] = paintedReplaceColor[3];
          } else {
            // 将有颜色的像素改为透明像素（旧版涂抹替换）
            data[i + 3] = 0;
          }
        } else {
          // 将无颜色的像素改为指定颜色
          data[i] = replaceColor[0];
          data[i + 1] = replaceColor[1];
          data[i + 2] = replaceColor[2];
          data[i + 3] = replaceColor[3];
        }
      }

      tempCtx.putImageData(imageData, 0, 0);
      return tempCanvas.toDataURL("image/png");
    }
    return "";
  } catch (error) {
    return "";
  }
}

// 去除对象无效字段
export function filteringInvalidFields(obj: object, field: string[] = []) {
  if (Object.prototype.toString.call(obj) === "[object Object]") {
    for (const key in obj) {
      if (!field.includes(key)) {
        delete obj[key as keyof typeof obj];
      }
    }
  }
}

// 获取文件md5用以检测上传的文件是否重复
import SparkMD5 from "spark-md5";
export function computeMD5(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const blobSlice = File.prototype.slice || (File.prototype as any).mozSlice || (File.prototype as any).webkitSlice,
      chunkSize = 2097152, // Read in chunks of 2MB
      chunks = Math.ceil(file.size / chunkSize),
      spark = new SparkMD5.ArrayBuffer(),
      fileReader = new FileReader();

    let currentChunk = 0;

    fileReader.onload = function (e: any) {
      spark.append(e.target.result); // Append array buffer
      currentChunk++;

      if (currentChunk < chunks) {
        loadNext();
      } else {
        resolve(spark.end());
      }
    };

    fileReader.onerror = function (error) {
      reject(error);
    };

    function loadNext() {
      const start = currentChunk * chunkSize,
        end = start + chunkSize >= file.size ? file.size : start + chunkSize;

      fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
    }

    loadNext();
  });
}

// 秒转时分秒
import dayjs from "dayjs";
// 时间插件
import duration from "dayjs/plugin/duration";
import { message, notification } from "ant-design-vue";
import { h } from "vue";
import { downloadFileByLink } from "./download";
import { nanoid } from "nanoid";

dayjs.extend(duration);
export function formatSeconds(seconds: number) {
  const duration = dayjs.duration(seconds, "seconds");
  const hours = String(duration.hours()).padStart(2, "0");
  const minutes = String(duration.minutes()).padStart(2, "0");
  const secs = String(duration.seconds()).padStart(2, "0");

  if (hours === "00") {
    return `${minutes}:${secs}`;
  }
  return `${hours}:${minutes}:${secs}`;
}

export function isMobileDevice() {
  return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * 检测设备类型
 * @returns 'mobile' | 'tablet' | 'desktop'
 */
export function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const ua = navigator.userAgent.toLowerCase();

  // 检测平板设备
  const isTablet = /ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP)))/i.test(ua);

  // 检测移动设备（排除平板）
  const isMobile = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua) && !isTablet;

  if (isMobile) {
    return "mobile";
  } else if (isTablet) {
    return "tablet";
  } else {
    return "desktop";
  }
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// 获取地址栏参数
export function getURLSearchParams(key: string) {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

export const delay = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));

// 获取 /assets/images目录下的文件地址
export const getAssetsImagePath = (name: string) => {
  // 使用相对路径 + import.meta.url
  // Vite6后都会出现这个问题,使用vite忽略处理
  const images = import.meta.glob("../assets/images/**/*", { eager: true, import: "default" });
  const path = `../assets/images/${name}`;

  try {
    return (images[path] as string) || "";
  } catch (error) {
    console.error("getAssetsImagePath error:", error);
    return `/assets/images/${name}`;
  }
};

/**
 * 检测是否为H5环境
 * 微信桌面端（Windows/Mac）不视为H5环境，避免将桌面微信用户错误重定向到移动端
 * @returns 是否为H5环境
 */
export const checkIsH5Environment = async (): Promise<boolean> => {
  if (typeof window === "undefined") {
    return false;
  }

  const ua = window.navigator.userAgent.toLowerCase();
  const isMobile = /mobile|android|iphone|ipad|phone/i.test(ua);

  // 桌面端微信（Windows/Mac 上的微信内置浏览器）不算 H5 环境
  const isDesktopWeChat = ua.includes("micromessenger") && !/android|iphone|ipad|ipod/i.test(ua);
  if (isDesktopWeChat) return false;

  return isMobile;
};
